import { useNavigate } from 'react-router-dom';
import { useVote } from '../../hooks/useCommunity';
import { useAuth } from '../../context/AuthContext';

/**
 * Heart toggle + vote count. Anonymous click sends the user to /login
 * with redirect=back; authenticated click optimistically toggles via
 * useVote.
 *
 * Size variants: 'sm' (32px, used on cards), 'lg' (48px, used on detail).
 */
export default function VoteButton({ coupon, size = 'sm', onChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { voted, count, toggle, busy } = useVote(coupon, onChange);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    toggle();
  };

  const dims = size === 'lg' ? { box: 48, icon: 24, count: 14 } : { box: 32, icon: 16, count: 12 };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-pressed={voted}
      aria-label={voted ? 'Remove your upvote' : 'Upvote this deal'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: voted ? 'linear-gradient(135deg, #EC4899, #F97316)' : '#fff',
        color: voted ? '#fff' : '#4A5568',
        border: `1.5px solid ${voted ? 'transparent' : '#E4E7F0'}`,
        borderRadius: 999,
        padding: size === 'lg' ? '10px 18px' : '6px 12px',
        cursor: busy ? 'wait' : 'pointer',
        fontSize: dims.count,
        fontWeight: 700,
        fontFamily: 'inherit',
        transition: 'transform 0.12s, box-shadow 0.12s',
        boxShadow: voted ? '0 6px 16px rgba(236,72,153,0.3)' : 'none',
      }}
      onMouseEnter={e => { if (!busy) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; }}
    >
      <span style={{ fontSize: dims.icon, lineHeight: 1 }} aria-hidden="true">
        {voted ? '❤️' : '🤍'}
      </span>
      <span>{count}</span>
    </button>
  );
}
